import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import useCommunityMapPins from '../../hooks/useCommunityMapPins'
import useProfileMapCenter from '../../hooks/useProfileMapCenter'
import { buildSocialLinks } from '../../lib/socialLinks'
import ProfileTemplate from './ProfileTemplate'
import EventSlideshowModal from '../../components/EventSlideshowModal'
import { ProfileMain } from '../../components/Profiles'
import { BusinessProfileLeftSidebar } from '../../components/Profiles/BusinessProfile'
import EventMultiplier from '../../components/Profiles/BusinessProfile/EventMultiplier/EventMultiplier'
import Loading from '../../components/loading'


function formatBusinessLocation(business) {
  if (!business) return ''
  const parts = [business.city, business.state].filter(Boolean)
  return parts.join(', ')
}


export default function BusinessProfilePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slugOrId } = useParams()
  const { currentUser } = useCurrentUser()
  const { getBusiness } = useApi()

  const [business, setBusiness] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const isMyBusinessRoute = !slugOrId
  const businessId = isMyBusinessRoute ? currentUser?.business_id : null

  useEffect(() => {
    if (isMyBusinessRoute && !businessId) {
      setIsLoading(false)
      return
    }
    if (!isMyBusinessRoute && !slugOrId) {
      setIsLoading(false)
      return
    }
    const identifier = isMyBusinessRoute ? businessId : slugOrId
    let cancelled = false
    setNotFound(false)
    const run = async () => {
      try {
        const { data } = await getBusiness(identifier)
        if (!cancelled && data) {
          setBusiness({ ...data, industry_id: data.industry?.id })
        } else if (!cancelled && !data) {
          setBusiness(null)
          if (!isMyBusinessRoute) setNotFound(true)
        }
      } catch (err) {
        if (!cancelled) {
          setBusiness(null)
          if (!isMyBusinessRoute) setNotFound(true)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [isMyBusinessRoute, businessId, slugOrId, getBusiness])

  const locationStr = business ? formatBusinessLocation(business) : ''
  const businessIdOrSlug = business ? (business.slug ?? business.id) : null
  const { center: mapCenter, loading: mapCenterLoading } = useProfileMapCenter({
    location: locationStr || null,
    enabled: !!locationStr,
  })
  const { pins: mapPins } = useCommunityMapPins('business', businessIdOrSlug)

  const [slideshowEvent, setSlideshowEvent] = React.useState(null)

  const profileUrl =
    business?.slug != null
      ? `${window.location.origin}/profile/business/${encodeURIComponent(business.slug)}`
      : null
  const onShareClick = useCallback(() => {
    if (!profileUrl) return
    navigator.clipboard.writeText(profileUrl).then(
      () => {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(t('share.copied', 'Profile link copied to clipboard.'))
        }
      },
      () => {},
    )
  }, [profileUrl, t])

  const handleOpenSlideshow = useCallback(
    ({ eventId, eventSlug }) => {
      setSlideshowEvent({
        id: eventId,
        slug: eventSlug || null,
      })
    },
    [],
  )

  const handleCloseSlideshow = useCallback(() => {
    setSlideshowEvent(null)
  }, [])

  if (isLoading) {
    return <Loading show />
  }

  if (notFound) {
    return (
      <div className="container py-5">
        <p className="text-muted">{t('profiles.businessNotFound', 'Business not found.')}</p>
      </div>
    )
  }

  if (isMyBusinessRoute && (!businessId || !business)) {
    navigate('/business-account', { replace: true })
    return null
  }

  if (!business) {
    return null
  }

  const industryName = business.industry
    ? (i18n.language === 'es' ? business.industry.es_name : business.industry.en_name) || business.industry.en_name
    : ''
  const logoUrl =
    business.media_attachment?.thumbnail_url ??
    business.media_attachment?.direct_upload_url ??
    null
  const coverImageUrl =
    business.cover_photo?.thumbnail_url ??
    business.cover_photo?.direct_upload_url ??
    undefined

  
  const isBusinessOwner =
    currentUser?.has_business && currentUser?.business_id === business.id

  return (
    <>
      <ProfileTemplate
        profileType="business"
        headerProps={{
          coverImageUrl,
          logoUrl,
          businessName: business.name,
          onUploadLogoClick: isBusinessOwner ? () => navigate('/business-account') : undefined,
          onEditPageClick: isBusinessOwner ? () => navigate('/business-account') : undefined,
          socialLinks: buildSocialLinks(business),
          isFollowing: false,
          onFollowClick: !isBusinessOwner ? () => {} : undefined,
          followLoading: false,
          onShareClick: profileUrl ? onShareClick : undefined,
        }}
        breadcrumbItems={[
          { label: t('profiles.breadcrumbHome', 'CR Homepage'), href: '/' },
          { label: t('profiles.breadcrumbIndustry', 'Industry') },
          { label: business.name },
        ]}
        leftSidebarContent={
          <BusinessProfileLeftSidebar
            name={business.name}
            location={locationStr}
            industryName={industryName}
            bio={business.community_statement}
            websiteUrl={business.website_url}
            followersCount={0}
            categories={business.categories}
            locale={currentUser?.language ?? i18n.language ?? 'en'}
          />
        }
        mainContent={
          <ProfileMain
            location={locationStr}
            mapCenter={mapCenter}
            mapCenterLoading={mapCenterLoading}
            profileType="business"
            mapPins={mapPins}
            onOpenSlideshow={handleOpenSlideshow}
          />
        }
        rightSidebarContent={
          <EventMultiplier
            businessId={business?.id}
            enabled={!!currentUser?.id}
            locale={currentUser?.language ?? i18n.language ?? 'en'}
          />
        }
      />
      {slideshowEvent && (
        <EventSlideshowModal
          show
          onHide={handleCloseSlideshow}
          eventId={slideshowEvent.id}
          eventSlug={slideshowEvent.slug}
        />
      )}
    </>
  )
}
